// ============================================================
//  Klassifikation nach PCE-Regelwerk — ECHTER CODE, kein Mock.
//  Dieselbe Funktion, die später einen Kundenexport verarbeitet:
//  Berechtigungswert → erste greifende Regel (nach Step) → Klasse.
//  Der Step ist die Prüfreihenfolge; die erste greifende Regel gewinnt,
//  dadurch setzt sich automatisch die höchste Klasse durch.
// ============================================================

import type { AuthValue, EngineMetric, LicenseClass } from "./types";
import { rulesForObject, type PceRule } from "../data/pce-ruleset";

// Developer wird über das #DEV#-Kennzeichen erkannt (Step 10, DUMMY).
const DEV_STEP = 10;

/** Passt eine Regel auf einen konkreten Berechtigungswert? Wildcard `*` greift. */
function matches(rule: PceRule, av: AuthValue): boolean {
  if (rule.type === "DUMMY") return false; // Developer separat über isDev
  if (rule.obj !== "*" && rule.obj !== av.obj) return false;
  if (rule.field != null && av.field != null && rule.field !== av.field) return false;
  if (rule.val != null && rule.val !== "*" && rule.val !== av.val) return false;
  return true;
}

/**
 * Erste greifende Regel für einen Wert (nach Step-Reihenfolge).
 *
 * Gesucht wird ausschließlich unter den Regeln des jeweiligen Auth-Objekts —
 * sie liegen bereits nach Step sortiert vor. Ein linearer Lauf über alle
 * 3.115 Regeln wäre bei über 100.000 Konten nicht tragbar; das Ergebnis ist
 * dasselbe, weil eine Regel nur auf ihr eigenes Objekt passt.
 */
export function classifyValue(av: AuthValue): PceRule | null {
  for (const rule of rulesForObject(av.obj)) {
    if (matches(rule, av)) return rule;
  }
  return null;
}

export interface Classification {
  cls: LicenseClass;
  step: number; // Step der ausschlaggebenden FUE-Regel (kleiner = höher)
  drivingValue: AuthValue | null;
  drivingRule: PceRule | null;
  engines: EngineMetric[];
}

/**
 * Klassifiziert eine Menge von Berechtigungswerten.
 * FUE-Klasse = Regel mit kleinstem Step (höchste Klasse). #DEV# ⇒ Developer.
 * Engine-Treffer werden separat gesammelt (nie FUE).
 */
export function classifyValues(values: AuthValue[], isDev = false): Classification {
  let best: PceRule | null = null;
  let bestValue: AuthValue | null = null;
  const engines = new Set<EngineMetric>();

  for (const av of values) {
    const rule = classifyValue(av);
    if (!rule) continue;
    if (rule.engine) engines.add(rule.engine);
    if (rule.cls == null) continue; // Engine/technisch → keine FUE-Klasse
    if (best === null || rule.step < best.step) {
      best = rule;
      bestValue = av;
    }
  }

  if (isDev) {
    // Developer überschreibt (Step 10 ist höher als jede AUTH-Regel).
    if (best === null || DEV_STEP < best.step) {
      return { cls: "Developer", step: DEV_STEP, drivingValue: bestValue, drivingRule: best, engines: [...engines] };
    }
  }

  if (best === null) {
    return { cls: "NotRelevant", step: 9999, drivingValue: null, drivingRule: null, engines: [...engines] };
  }
  return { cls: best.cls as LicenseClass, step: best.step, drivingValue: bestValue, drivingRule: best, engines: [...engines] };
}

export const SELF_SERVICE_STEP = 300;

/**
 * Klassifiziert ein KONTO. Untergrenze: Ein Dialogbenutzer mit Rollen auf einem FUE-System ist
 * mindestens Self-Service — auch wenn kein Wert eine höhere Regel trifft. Unter Self-Service gibt
 * es nichts; „abstufen" endet dort. Rollen werden weiter mit classifyValues bewertet (dort ist
 * NotRelevant richtig: eine Rolle ohne Regeltreffer trägt keine Klasse).
 */
export function classifyAccount(values: AuthValue[], isDev: boolean | undefined, hasRoles: boolean): Classification {
  const c = classifyValues(values, isDev);
  if (c.cls === "NotRelevant" && hasRoles) return { ...c, cls: "SelfService", step: SELF_SERVICE_STEP };
  return c;
}

/** Höhere von zwei Klassen anhand des Step (kleiner = höher). NotRelevant = niedrigste. */
export function higherByStep(a: { cls: LicenseClass; step: number }, b: { cls: LicenseClass; step: number }) {
  return a.step <= b.step ? a : b;
}
